// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which tier this session runs, and why (M3-35, 08 §6 steps 3 and 4).
//
// `capability.ts` measures the device and `tiers.ts` names the tiers; this decides between
// them and remembers the answer. Four sources, in strict order of authority:
//
//   1. **An override.** The player said so in settings. Nothing outranks that — not a probe,
//      not a measurement, not the governor. A player who forced `low` on a laptop that runs
//      hot did not want to be argued with three seconds later.
//   2. **A stored choice**, from a previous visit on the same hardware. Saves the visible
//      second of running at the wrong tier while a calibration happens.
//   3. **A calibration**, once there is a machine on screen to time.
//   4. **The probe's ceiling**, which is what the first frame is drawn at.
//
// **What is deliberately not remembered: a governor step.** The governor (M3-36) reacts to
// *this session* — another tab compositing video, a game in multiball with every flasher lit, a
// laptop that has been on a duvet for ten minutes. Persisting that would let one bad afternoon
// permanently downgrade a device with no way back up and nothing on screen to say why. The
// calibration measures the hardware and is stored; the governor measures the moment and is not.
//
// Storage is injected. `localStorage` is the default and M3-39 owns the real persistence layer;
// what this needs from it is three methods, so it asks for three methods rather than for a
// module that does not exist yet.

import {
  deviceFingerprint,
  probeCapability,
  tierCeiling,
  type Capability,
} from './capability'
import { RENDER_TIERS, type RenderTier } from './tiers'
import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'

export type { StorageLike }

/** Where the choice is kept. One key, so it cannot be spelled two ways. */
export const TIER_STORAGE_KEY = 'hcc.pinball.render-tier'

/** How the current tier came to be chosen. Shown in settings, and in the harness HUD. */
export type TierSource = 'override' | 'stored' | 'calibrated' | 'probe' | 'governor'

interface StoredRecord {
  /** The measured choice, or absent when only an override was ever set. */
  tier?: RenderTier
  /** The player's forced tier, if any. Survives a device change; a measured tier does not. */
  override?: RenderTier
  /** What the device looked like when `tier` was measured. */
  fingerprint?: string
}

function isTier(value: unknown): value is RenderTier {
  return typeof value === 'string' && (RENDER_TIERS as readonly string[]).includes(value)
}

export interface TierChoiceOptions {
  /** Skip the browser probe — a test supplies the device instead of having one. */
  readonly capability?: Capability
  /** Where to remember the choice. `undefined` means do not remember it. */
  readonly storage?: StorageLike | undefined
  /** Start from a forced tier, as `?tier=low` or a settings value would. */
  readonly forced?: RenderTier
}

/**
 * The tier this session is running, and everything that decided it.
 *
 * A small object with a long memory rather than a function, because the decision is made in
 * stages that are minutes apart: the probe runs at load, the calibration runs on the first
 * frames of the 3-D view, the player may open settings at any point, and the governor may step
 * in at any point after that. Something has to hold the reasoning between those moments, or
 * each one re-derives it and they disagree.
 */
export class TierChoice {
  readonly capability: Capability
  readonly fingerprint: string

  private readonly storage: StorageLike | undefined
  private readonly ceilingReasons: readonly string[]
  private readonly ceilingTier: RenderTier
  private forced: RenderTier | undefined
  private current: RenderTier
  private origin: TierSource

  constructor(options: TierChoiceOptions = {}) {
    this.capability = options.capability ?? probeCapability()
    this.fingerprint = deviceFingerprint(this.capability)
    this.storage = 'storage' in options ? options.storage : safeStorage()

    const { ceiling, reasons } = tierCeiling(this.capability)
    this.ceilingTier = ceiling
    this.ceilingReasons = reasons

    const saved = this.read()
    this.forced = options.forced ?? saved.override
    if (options.forced !== undefined) this.write({ ...saved, override: options.forced })

    if (this.forced !== undefined) {
      this.current = this.forced
      this.origin = 'override'
    } else if (saved.tier !== undefined) {
      this.current = saved.tier
      this.origin = 'stored'
    } else {
      this.current = ceiling
      this.origin = 'probe'
    }
  }

  /** The tier to render at, right now. */
  get tier(): RenderTier {
    return this.current
  }

  /** What decided it. */
  get source(): TierSource {
    return this.origin
  }

  /** The highest tier the probe permits. A calibration may go below it and never above it. */
  get ceiling(): RenderTier {
    return this.ceilingTier
  }

  /** Why the ceiling is where it is. Empty on a device with nothing against it. */
  get reasons(): readonly string[] {
    return this.ceilingReasons
  }

  /** The player's forced tier, if they have set one. */
  get override(): RenderTier | undefined {
    return this.forced
  }

  /**
   * Is a calibration still worth running?
   *
   * No, when the player has forced a tier — measuring something whose answer cannot be acted on
   * is two wasted frames at the worst possible moment, the first two of a machine load.
   * Also no when a stored choice from this same hardware already exists, which is the whole
   * point of storing it.
   */
  get needsCalibration(): boolean {
    return this.origin === 'probe'
  }

  /**
   * Record the tier a calibration settled on (08 §6 step 2) and store it.
   *
   * The *settling* is the caller's, because it is a loop of measure-step-measure rather than a
   * calculation — see `fitsFrameBudget`. What belongs here is the memory: the tier, and the
   * fingerprint of the device it was measured on, so the next visit can tell whether it is
   * still describing the same machine.
   *
   * Refuses to rise above the ceiling. A calibration that came back with `high` on a device the
   * probe capped at `medium` would mean the caller measured a tier it was never allowed to run,
   * and storing that would make the mistake permanent.
   */
  calibrated(tier: RenderTier): RenderTier {
    if (this.forced !== undefined) return this.current
    const allowed =
      RENDER_TIERS.indexOf(tier) >= RENDER_TIERS.indexOf(this.ceilingTier) ? tier : this.ceilingTier
    this.current = allowed
    this.origin = 'calibrated'
    this.write({ ...this.read(), tier: allowed, fingerprint: this.fingerprint })
    return allowed
  }

  /**
   * The governor stepped down (M3-36). Recorded, **not** stored — see this file's header.
   *
   * Refuses to move under an override: a player who forced a tier is entitled to a machine that
   * stutters at the tier they asked for.
   */
  stepped(tier: RenderTier): boolean {
    if (this.forced !== undefined || tier === this.current) return false
    this.current = tier
    this.origin = 'governor'
    return true
  }

  /**
   * The settings panel. `undefined` clears the override and hands the decision back.
   *
   * Clearing returns to the stored measurement where there is one and to the ceiling otherwise,
   * rather than to whatever the governor had most recently chosen — the point of clearing an
   * override is to ask the question again, and a governor step is an answer about a moment that
   * has passed.
   */
  setOverride(tier: RenderTier | undefined): RenderTier {
    this.forced = tier
    const saved = this.read()
    if (tier === undefined) {
      const { override: _dropped, ...rest } = saved
      this.write(rest)
      const stored = rest.tier
      this.current = stored ?? this.ceilingTier
      this.origin = stored === undefined ? 'probe' : 'stored'
    } else {
      this.write({ ...saved, override: tier })
      this.current = tier
      this.origin = 'override'
    }
    return this.current
  }

  /** One line for the settings panel and the HUD: the tier, where it came from, and why. */
  describe(): string {
    const why = this.ceilingReasons.length > 0 ? ` (${this.ceilingReasons.join(', ')})` : ''
    return `${this.current} · ${this.origin}${this.origin === 'override' ? '' : why}`
  }

  /**
   * Read the stored record, discarding a measurement taken on different hardware.
   *
   * The **override survives** a fingerprint change and the measurement does not. They are
   * different kinds of claim: a measurement is a statement about a device, and is worthless
   * once the device has changed; an override is a statement about a person, and a player who
   * prefers a calmer machine still prefers one after a driver update.
   */
  private read(): StoredRecord {
    const raw = readLocal(this.storage, TIER_STORAGE_KEY)
    if (raw === undefined) return {}
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      // Somebody else's key, or a half-written value from a tab that was closed mid-write.
      return {}
    }
    if (typeof parsed !== 'object' || parsed === null) return {}
    const record = parsed as Record<string, unknown>
    const out: StoredRecord = {}
    if (isTier(record['override'])) out.override = record['override']
    if (isTier(record['tier']) && record['fingerprint'] === this.fingerprint) {
      out.tier = record['tier']
      out.fingerprint = this.fingerprint
    }
    return out
  }

  private write(record: StoredRecord): void {
    if (record.tier === undefined && record.override === undefined) {
      removeLocal(this.storage, TIER_STORAGE_KEY)
    } else {
      writeLocal(this.storage, TIER_STORAGE_KEY, JSON.stringify(record))
    }
  }
}
